import Alpine from 'alpinejs'
import collapse from '@alpinejs/collapse'
import Flickity from 'flickity'
import { gsap } from 'gsap'

window.Alpine = Alpine
Alpine.plugin(collapse)
Alpine.start()

// Hero headline animation
function initHeroAnimation() {
    const heroHeadline = document.querySelector('.hero-block h1');
    const heroText = document.querySelector('.hero-block .text-base, .hero-block .md\\:text-xl');
    const heroButton = document.querySelector('.hero-block a[href]');

    if (heroHeadline) {
        // Set initial state - headline positioned off-screen to the left
        gsap.set(heroHeadline, {
            x: -100,
            opacity: 0,
            visibility: 'visible' // Make visible for animation
        });

        // Animate headline from left to its final position
        gsap.to(heroHeadline, {
            x: 0,
            opacity: 1,
            duration: 0.8, // Reduced from 1.2 to 0.8
            ease: "power2.out",
            delay: 0.2 // Reduced from 0.3 to 0.2
        });
    }

    if (heroText) {
        // Set initial state - text positioned below and invisible
        gsap.set(heroText, {
            y: 50,
            opacity: 0,
            visibility: 'visible' // Make visible for animation
        });

        // Animate text from bottom to its final position
        gsap.to(heroText, {
            y: 0,
            opacity: 1,
            duration: 0.8, // Reduced from 1.2 to 0.8
            ease: "power2.out",
            delay: 0.5 // Reduced from 0.8 to 0.5
        });
    }

    if (heroButton) {
        // Set initial state - button positioned below and invisible
        gsap.set(heroButton, {
            y: 50,
            opacity: 0,
            visibility: 'visible' // Make visible for animation
        });

        // Animate button from bottom to its final position
        gsap.to(heroButton, {
            y: 0,
            opacity: 1,
            duration: 0.8, // Reduced from 1.2 to 0.8
            ease: "power2.out",
            delay: 0.8 // Reduced from 1.3 to 0.8
        });
    }
}

// Header scroll animation
function initHeaderAnimation() {
    const header1 = document.getElementById('header-1');
    const header2 = document.getElementById('header-2');

    if (!header1 || !header2) return;

    let ticking = false;

    function updateHeader() {
        const scrollY = window.scrollY;

        if (scrollY > 100) {
            // Show header 2, hide header 1
            header1.style.opacity = '0';
            header1.style.pointerEvents = 'none';
            header2.style.opacity = '1';
            header2.style.pointerEvents = 'auto';
        } else {
            // Show header 1, hide header 2
            header1.style.opacity = '1';
            header1.style.pointerEvents = 'auto';
            header2.style.opacity = '0';
            header2.style.pointerEvents = 'none';
        }

        ticking = false;
    }

    function requestTick() {
        if (!ticking) {
            requestAnimationFrame(updateHeader);
            ticking = true;
        }
    }

    window.addEventListener('scroll', requestTick);
}

// Initialize testimonials slider
function initTestimonialsSlider() {
    const testimonialsSlider = document.querySelector('.testimonials-slider');
    if (testimonialsSlider) {
        const flkty = new Flickity(testimonialsSlider, {
            cellSelector: '.testimonial-slide',
            cellAlign: 'left',
            slidesToShow: 2,
            pageDots: false,
            contain: true,
            imagesLoaded: true,
            draggable: false,
            prevNextButtons: false,
            wrapAround: true
        });

        // Set up navigation buttons
        const nextButton = testimonialsSlider.parentElement.querySelector('.slider-next');
        if (nextButton) {
            nextButton.addEventListener('click', function() {
                flkty.next();
            });
        }

        const prevButton = testimonialsSlider.parentElement.querySelector('.slider-prev');
        if (prevButton) {
            prevButton.addEventListener('click', function() {
                flkty.previous();
            });
        }
    }
}

// Video autoplay functionality
function initVideoAutoplay() {
    const videos = document.querySelectorAll('video[controls]');

    if (videos.length === 0) return;

    const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const video = entry.target;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.05) {
                // Video is 20% visible - start playing
                video.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Autoplay prevented:', error);
                });
            } else {
                // Video is not visible enough or outside viewport - pause
                video.pause();
                video.currentTime = 0; // Reset to beginning
            }
        });
    }, {
        threshold: 0.2, // Trigger when 20% visible
        rootMargin: '0px'
    });

    // Observe all videos
    videos.forEach(video => {
        videoObserver.observe(video);
    });
}

// Fade-in animation for elements with .fade-in class
function initFadeInAnimations() {
    const fadeElements = document.querySelectorAll('.fade-in');

    if (fadeElements.length === 0) return;

    // Set initial state for all fade-in elements
    gsap.set(fadeElements, {
        y: 30,
        opacity: 0
    });

    // Create intersection observer
    const fadeObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const target = entry.target;

                // Check if this element contains multiple child elements that should stagger
                const staggerChildren = target.querySelectorAll('.fade-in > *');

                if (staggerChildren.length > 1) {
                    // Animate the parent container first
                    gsap.to(target, {
                        y: 0,
                        opacity: 1,
                        duration: 0.8,
                        ease: "power2.out",
                        delay: 0.1
                    });

                    // Then stagger animate the children
                    gsap.to(staggerChildren, {
                        y: 0,
                        opacity: 1,
                        duration: 0.6,
                        ease: "power2.out",
                        stagger: 0.1, // 0.1 second delay between each child
                        delay: 0.3 // Start after parent animation
                    });
                } else {
                    // Single element animation
                    gsap.to(target, {
                        y: 0,
                        opacity: 1,
                        duration: 0.8,
                        ease: "power2.out",
                        delay: 0.1
                    });
                }

                // Stop observing this element after animation
                fadeObserver.unobserve(target);
            }
        });
    }, {
        threshold: 0.1, // Trigger when 10% of element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element is fully in view
    });

    // Observe all fade-in elements
    fadeElements.forEach(element => {
        fadeObserver.observe(element);
    });
}

window.addEventListener('load', function () {
    // Initialize hero animation
    initHeroAnimation();

    // Initialize header animation
    initHeaderAnimation();

    // Initialize testimonials slider
    initTestimonialsSlider();

    // Initialize video autoplay
    initVideoAutoplay();

    // Initialize fade-in animations
    initFadeInAnimations();

    var form = document.querySelector('form');
    var message = document.getElementById('message');
    var fields = {};
	if (form) {
		form.querySelectorAll('[name]').forEach(function (field) {
			fields[field.name] = field;
		});

		// Displays all error messages and adds 'error' classes to the form fields with
		// failed validation.
		var handleError = function (response) {
			var errors = [];
			for (var key in response) {
				if (!response.hasOwnProperty(key)) continue;
				if (fields.hasOwnProperty(key)) fields[key].classList.add('error');
				Array.prototype.push.apply(errors, response[key]);
			}
			message.innerHTML = errors.join('<br>');
		}

		var onload = function (e) {
			if (e.target.status === 200) {
				message.innerHTML = 'Ihre E-Mail wurde erfolgreich versendet!'
			} else {
				handleError(JSON.parse(e.target.response));
			}
		};

		var submit = function (e) {
			e.preventDefault();
			var request = new XMLHttpRequest();
			request.open('POST', e.target.action);
			request.onload = onload;
			request.send(new FormData(e.target));
			// Remove all 'error' classes of a possible previously failed validation.
			for (var key in fields) {
				if (!fields.hasOwnProperty(key)) continue;
				fields[key].classList.remove('error');
			}
		};
		form.addEventListener('submit', submit);
	}
});